import dayjs from 'dayjs';
import camelCase from 'lodash/camelCase';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import { numericFormatter } from 'react-number-format';

import { Maybe } from '../apis/graphql/types';
import { DayPartTimeRanges } from '../components/ScheduleCalendar/TimeList/types';

export const orderedDaysOfWeek: string[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export function formatTruncatedDay(formatDay: string): string {
  return formatDay.charAt(0).toUpperCase() + formatDay.slice(1).slice(0, 2).toLowerCase();
}

export const formatMilitaryTime = (time: number, rangeType?: 'first' | 'last'): string => {
  if (time === 0) {
    if (rangeType) {
      return rangeType === 'first' ? '12:00am' : '12:59am';
    }
    return '12am';
  }

  const suffix = time >= 12 && time <= 23 ? 'pm' : 'am';
  const minutes = rangeType ? (rangeType === 'first' ? ':00' : ':59') : '';

  return time > 12 ? `${time - 12}${minutes}${suffix}` : `${time}${minutes}${suffix}`;
};

export const formatTimeRanges = (ranges: DayPartTimeRanges, isShowMinutes?: boolean): string => {
  if (!ranges || ranges.length === 0) return 'Dark';
  const formattedTimeString = ranges.map((range: [number, number]) => {
    if (range[0] === 0 && range[1] === 23) return 'All day';

    if (isShowMinutes) {
      return `${formatMilitaryTime(range[0], 'first')} - ${formatMilitaryTime(range[1], 'last')}`;
    } else {
      return `${formatMilitaryTime(range[0])} - ${formatMilitaryTime(range[1])}`;
    }
  });

  return formattedTimeString.join(', ');
};

export const convertToDataTestId = (value: string, postfix: string): string => {
  if (value) {
    const valueWithoutSpaces = value.replace(' ', '');
    const camelCaseValue = valueWithoutSpaces.charAt(0).toLowerCase() + valueWithoutSpaces.slice(1);

    return `${camelCaseValue}${postfix}`;
  }

  return '';
};

export const toHHMMSS = (originalInSeconds: number): string =>
  new Date(originalInSeconds * 1000).toISOString().slice(11, 23);

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (!+bytes) return '0 Bytes';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const numberToLocaledString = (figure: number): string => {
  return Number(figure).toLocaleString();
};

export const statusFormatter = (status: string): string => status.replaceAll('_', ' ');

export function formatCapitalizeUnderscoreString(str: string): string {
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1))
    .join(' ');
}

export const getFrequencyString = (frequencyCap: Maybe<number>, frequencyCapUnit: Maybe<string>): string => {
  return frequencyCap && frequencyCapUnit ? frequencyCap + ' Ads/' + frequencyCapUnit : '—';
};
export const getNumberToSum = (number?: Maybe<number>): string | null => {
  return number ? '$' + numericFormatter(number.toFixed(2), { thousandSeparator: true }) : null;
};
export const getTargetImpressions = (
  deliveredImpressions?: Maybe<number>,
  targetImpressions?: Maybe<number> | undefined
): string => {
  const delivered = deliveredImpressions
    ? numericFormatter(String(deliveredImpressions), { thousandSeparator: true })
    : '-';
  const target = targetImpressions ? numericFormatter(String(targetImpressions), { thousandSeparator: true }) : '-';
  return `${delivered}/${target}`;
};

type FormatNumberProps = {
  figure: number;
  dollarValue?: boolean;
  percentageValue?: boolean;
  figuresAfterComma?: number;
};

export const formatNumber = ({
  figure,
  dollarValue,
  percentageValue,
  figuresAfterComma = 2,
}: FormatNumberProps): string => {
  const numberToFixed = figure.toFixed(figuresAfterComma);

  return `${dollarValue ? '$' : ''}${Number(numberToFixed).toLocaleString()}${percentageValue ? '%' : ''}`;
};

export const capitalizeUnderScoreWord = (word: string): string => startCase(camelCase(word));

export const formatDateToMMDDYYYY = (date: string): string => dayjs(date).format('MM/DD/YYYY');

export const formatToNonMicros = (num: number): number => num / 1000000;

export const parseFormikErrors = (errors: Record<string, unknown>): string =>
  Object.values(errors)
    .map((error) => capitalize(error as string))
    .join(', ');

export const getFrequencyFormatted = (frequency: string): string => frequency.toLowerCase().replace('/', ' / ');
